// ─────────────────────────────────────────────────────────────────────────────
// EligiblesFilterBar — the spec table-B filter bar, extracted so the SAME UI drives
// the Éligibles, Export and Validation screens (Gerd round-2: "the Validation /
// Export screens should allow the same filters as Éligibles"). Pure presentation:
// it derives the dropdown options from the RAW view and reports changes up; the
// parent owns the `filters`/`month` state and applies the shared `filterEligiblesView`.
//
// `streams` selects which stream-specific groups to show (the active tab on a tabbed
// screen): SSV → SSV Done/Validation/Date, TSA → TSA Done/Validation/Date. Contractor
// + Area apply to both and are always shown (Contractor only for staff — a provider
// is auto-scoped). The free-text search is optional (pass `onQueryChange`).
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, type ReactNode } from 'react';
import { Search, RotateCcw } from 'lucide-react';
import type { EligiblesView, EligiblesTabRow, EligiblesFilters } from '@mdu-invoicing/shared';
import { cn } from '@/lib/cn';

type Stream = 'ssv' | 'tsa';

/** Shared <select>/<input> styling for the filter bar. */
const filterControlClass =
  'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20';

/** 'YYYY-MM-DD'(THH:MM…) → 'DD/MM/YYYY' for the date-option labels. */
function frDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/** A labelled filter control (label above the control). */
function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </label>
  );
}

export interface EligiblesFilterBarProps {
  /** The RAW (unfiltered) view — dropdown options derive from it, so they stay stable. */
  view: EligiblesView | null;
  filters: EligiblesFilters;
  /** Set one filter field ('' clears it). */
  onChange: (key: keyof EligiblesFilters, value: string) => void;
  /** Clear all filters (and the search, if the parent wires it). */
  onReset: () => void;
  month: string;
  onMonthChange: (v: string) => void;
  /** Staff see the Contractor filter; a provider is auto-scoped (no Contractor control). */
  isProvider: boolean;
  /** Which stream-specific filter groups to show (typically the active tab). */
  streams: Stream[];
  /** Optional free-text search (MDU_ID / area / address). */
  query?: string;
  onQueryChange?: (v: string) => void;
  /** G2-13b: show the "Soumis / Non soumis" filter (staff, on Éligibles). */
  showSubmissionFilter?: boolean;
  /** G2-13b: show the "Fichier importé" filter (Gerd: select the previously imported file). */
  showImportFileFilter?: boolean;
}

export function EligiblesFilterBar({
  view,
  filters,
  onChange,
  onReset,
  month,
  onMonthChange,
  isProvider,
  streams,
  query,
  onQueryChange,
  showSubmissionFilter = false,
  showImportFileFilter = false,
}: EligiblesFilterBarProps) {
  // Options derive from the RAW view, so the lists stay stable regardless of selection.
  const options = useMemo(() => {
    const ssv = view?.ssv ?? [];
    const tsa = view?.tsa ?? [];
    const all = [...ssv, ...tsa];
    const uniqSorted = (vals: (string | null | undefined)[]) =>
      [...new Set(vals.filter((v): v is string => typeof v === 'string' && v.trim() !== ''))].sort((a, b) =>
        a.localeCompare(b),
      );
    const areaOf = (r: EligiblesTabRow) => r.source.areaName ?? r.source.areaId;
    return {
      contractors: uniqSorted(all.map((r) => r.source.ssvContractor)),
      areas: uniqSorted(all.map(areaOf)),
      ssvDone: uniqSorted(ssv.map((r) => r.source.ssvDone)),
      tsaDone: uniqSorted(tsa.map((r) => r.source.tsaDone)),
      ssvValidation: uniqSorted(ssv.map((r) => r.source.ssvValidationStatus)),
      tsaValidation: uniqSorted(tsa.map((r) => r.source.tsaValidationStatus)),
      ssvDates: uniqSorted(ssv.map((r) => (r.source.ssvDate ?? '').slice(0, 10) || null)),
      tsaDates: uniqSorted(tsa.map((r) => (r.source.tsaDate ?? '').slice(0, 10) || null)),
      // Distinct import batches present in the month's rows (G2-13b file filter). Keyed
      // by batch id; label = filename + import date; newest first.
      importFiles: (() => {
        const m = new Map<string, { filename: string; importedAt: string }>();
        for (const r of all) {
          const d = r.decision;
          if (d?.importBatchId) {
            m.set(d.importBatchId, { filename: d.importFilename ?? d.importBatchId, importedAt: d.importedAt ?? '' });
          }
        }
        return [...m.entries()]
          .map(([id, v]) => ({ id, ...v }))
          .sort((a, b) => b.importedAt.localeCompare(a.importedAt));
      })(),
    };
  }, [view]);

  const showSsv = streams.includes('ssv');
  const showTsa = streams.includes('tsa');
  const hasSearch = typeof onQueryChange === 'function';
  const activeFilterCount = Object.values(filters).filter(
    (v) => typeof v === 'string' && v.trim() !== '',
  ).length;
  const showReset = activeFilterCount > 0 || (query ?? '').trim() !== '';

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <FilterField label="Mois de facturation">
        <input
          type="month"
          value={month}
          onChange={(e) => onMonthChange(e.target.value)}
          aria-label="Mois de facturation"
          className={filterControlClass}
        />
      </FilterField>

      {!isProvider && (
        <FilterField label="Contractant">
          <select
            value={filters.contractor ?? ''}
            onChange={(e) => onChange('contractor', e.target.value)}
            aria-label="Filtrer par contractant"
            className={filterControlClass}
          >
            <option value="">Tous</option>
            {options.contractors.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </FilterField>
      )}

      <FilterField label="Area Name">
        <select
          value={filters.area ?? ''}
          onChange={(e) => onChange('area', e.target.value)}
          aria-label="Filtrer par Area Name"
          className={filterControlClass}
        >
          <option value="">Toutes</option>
          {options.areas.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </FilterField>

      {showSsv && (
        <>
          <FilterField label="SSV Done">
            <select
              value={filters.ssvDone ?? ''}
              onChange={(e) => onChange('ssvDone', e.target.value)}
              aria-label="Filtrer par SSV Done"
              className={filterControlClass}
            >
              <option value="">Tous</option>
              {options.ssvDone.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Statut validation (PBI)">
            <select
              value={filters.ssvValidation ?? ''}
              onChange={(e) => onChange('ssvValidation', e.target.value)}
              aria-label="Filtrer par statut de validation (PBI)"
              className={filterControlClass}
            >
              <option value="">Toutes</option>
              {options.ssvValidation.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Date d'upload">
            <select
              value={filters.ssvDate ?? ''}
              onChange={(e) => onChange('ssvDate', e.target.value)}
              aria-label="Filtrer par date d'upload SSV"
              className={filterControlClass}
            >
              <option value="">Toutes</option>
              {options.ssvDates.map((v) => (
                <option key={v} value={v}>
                  {frDate(v)}
                </option>
              ))}
            </select>
          </FilterField>
        </>
      )}

      {showTsa && (
        <>
          <FilterField label="TSA Done">
            <select
              value={filters.tsaDone ?? ''}
              onChange={(e) => onChange('tsaDone', e.target.value)}
              aria-label="Filtrer par TSA Done"
              className={filterControlClass}
            >
              <option value="">Tous</option>
              {options.tsaDone.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Statut validation (PBI)">
            <select
              value={filters.tsaValidation ?? ''}
              onChange={(e) => onChange('tsaValidation', e.target.value)}
              aria-label="Filtrer par statut de validation (PBI)"
              className={filterControlClass}
            >
              <option value="">Toutes</option>
              {options.tsaValidation.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Date d'upload">
            <select
              value={filters.tsaDate ?? ''}
              onChange={(e) => onChange('tsaDate', e.target.value)}
              aria-label="Filtrer par date d'upload TSA"
              className={filterControlClass}
            >
              <option value="">Toutes</option>
              {options.tsaDates.map((v) => (
                <option key={v} value={v}>
                  {frDate(v)}
                </option>
              ))}
            </select>
          </FilterField>
        </>
      )}

      {showSubmissionFilter && (
        <FilterField label="Soumission">
          <select
            value={filters.submission ?? ''}
            onChange={(e) => onChange('submission', e.target.value)}
            aria-label="Filtrer par état de soumission"
            className={filterControlClass}
          >
            <option value="">Toutes</option>
            <option value="submitted">Soumis</option>
            <option value="pending">Non soumis</option>
          </select>
        </FilterField>
      )}

      {showImportFileFilter && (
        <FilterField label="Fichier importé">
          <select
            value={filters.importFile ?? ''}
            onChange={(e) => onChange('importFile', e.target.value)}
            aria-label="Filtrer par fichier importé"
            className={filterControlClass}
          >
            <option value="">Tous</option>
            {options.importFiles.map((f) => (
              <option key={f.id} value={f.id}>
                {f.filename}
                {f.importedAt ? ` — ${frDate(f.importedAt)}` : ''}
              </option>
            ))}
          </select>
        </FilterField>
      )}

      {hasSearch && (
        <FilterField label="Recherche">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              value={query ?? ''}
              onChange={(e) => onQueryChange?.(e.target.value)}
              aria-label="Rechercher"
              placeholder="MDU_ID, zone, adresse…"
              className={cn(filterControlClass, 'w-56 pl-9')}
            />
          </div>
        </FilterField>
      )}

      {showReset && (
        <button
          type="button"
          onClick={onReset}
          className="mb-0.5 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Réinitialiser
        </button>
      )}
    </div>
  );
}
